/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from 'react';

/**
 * This import forces Parcel to keep @babel/runtime in the main bundle.
 * babel runtime is required by react spectrum.
 */
import '@react/react-spectrum/Provider';

const deferredComponents = import('./js-deferred');

/*
The ImportManager is a middleware between app logic and dynamic modules.
It allows optimizing bundle hierarchy without having to change any app logic using these modules.

Current bundle hierarchy:

1. Deferred (js-deferred.js)
  1.1. Login (Login.js)
  1.2. ShellComponents (ShellComponents.js) - Shell Toasts, Shell Header and Pulse UI
    1.2.1 NPSWidget (@exc/nps)
    1.2.2 ShellUserProfile (ShellUserProfile.js)
    1.2.3 ShellOrgSwitcher (ShellOrgSwitcher.js)
    1.2.4 SolutionSwitcher (SolutionSwitcher.js)
    1.2.5 HelpCenter (HelpCenter.js)
    1.2.6 CoachMark (@react/react-spectrum/CoachMark)
  1.3. SettingsService (@exc/settings)
  1.4. PulseService (@exc/pulse-sdk)
  1.5. NotFound (NotFound.js)
  1.6. ShellNoAccess (ShellNoAccess.js)
  1.7. CaasFrame (CaasFrame.js)

 */

/**
 * Get a first or second level deferred component
 * @param {string} First level component
 * @param {string} Second level component (Optional)
 * @returns {function(): Promise<unknown>} async function returning the component.
 */
function getDeferred(component, nestedComponent) {
  return () => deferredComponents.then(deferred => deferred[component])
    .then(deferredComponent =>
      nestedComponent ? deferredComponent[nestedComponent] : deferredComponent);
}

/**
 * Lazy loads a React component using React.lazy.
 * Loads JS deferred first and then lazy loads the component.
 * This two step approach helps keep the Shell main bundle smaller by moving logic to JS deferred.
 * @param {string} Component to be loaded.
 * @returns {Object} React.lazy wrapped component.
 */
function lazy(component) {
  return React.lazy(getDeferred(component));
}

export const ErrorView = lazy('ErrorView');
export const Login = lazy('Login');
export const ShellNoAccess = lazy('ShellNoAccess');
export const getCaasFrame = getDeferred('CaasFrame', 'default');
export const getPulseServiceFactory = getDeferred('PulseService', 'default');
export const getSettingsService = getDeferred('SettingsService');
export const getShellComponents = getDeferred('ShellComponents');
export const getShowShellToast = getDeferred('ShellComponents', 'showShellToast');
